import {
  AfterViewInit,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild, ViewContainerRef,
  ViewEncapsulation
} from '@angular/core';
import {
  ActionEventArgs,
  AgendaService, CellClickEventArgs,
  DayService,
  DragAndDropService, EventSettingsModel,
  ExcelExportService, GroupModel,
  ICalendarExportService,
  ICalendarImportService,
  MonthAgendaService,
  MonthService,
  ResizeService, ResourceDetails, ScheduleComponent,
  TimelineMonthService,
  TimelineViewsService,
  TimelineYearService, ToolbarActionArgs,
  WeekService,
  WorkWeekService,
  YearService
} from "@syncfusion/ej2-angular-schedule";
import {CheckBoxSelectionService} from "@syncfusion/ej2-angular-dropdowns";
import {
  AccordionAnimationSettingsModel,
  AccordionComponent, ClickEventArgs,
  ExpandMode, ItemModel, TabAnimationSettingsModel,
  TabComponent, TreeViewComponent
} from "@syncfusion/ej2-angular-navigations";
import {ScheduleModel} from "../../core/services/schedule-data/schedule.model";
import {KnockoutFieldModel} from "../../core/components/knockout-field/knockout-field.model";
import {closest, remove, addClass, removeClass, createElement} from "@syncfusion/ej2-base";
import {Subject, Subscription} from "rxjs";
import {ScheduleConfigurationModel} from "../../core/services/schedule-data/schedule-configuration.model";
import {ActivatedRoute} from "@angular/router";
import {UtilService} from "../../core/services/util/util.service";
import {RequestService} from "../../core/services/request/request.service";
import {ScheduleDataService} from "../../core/services/schedule-data/schedule-data.service";
import {LegacyNavigateService} from "../../core/services/legacy-navigate/legacy-navigate.service";
import {ChangeEventArgs as SwitchEventArgs} from "@syncfusion/ej2-buttons/src/common/common";
import {ScheduleEventModel} from "../../core/services/schedule-data/schedule-event.model";
import {cloneDeep} from 'lodash';
import {FieldsSettingsModel} from "@syncfusion/ej2-navigations/src/treeview/treeview-model";
import {DialogUtility} from "@syncfusion/ej2-angular-popups";
import {DrawNodeEventArgs} from "@syncfusion/ej2-navigations/src/treeview/treeview";
import { ResizeObserver } from '@juggle/resize-observer';
import {FeatureFlagsService} from "../../core/services/feature-flag/feature-flags.service";
import {TemplateCompilerOptions} from "../../core/services/template-compiler/template-compiler-options";
import {DragEventArgs} from "@syncfusion/ej2-schedule/src/schedule/base/interface";
import {MaskedTextBoxComponent} from "@syncfusion/ej2-angular-inputs";
import {DataManager, Predicate, Query} from "@syncfusion/ej2-data";
import {ChangedEventArgs} from "@syncfusion/ej2-inputs/src/textbox/textbox";
import {InternationalizationService} from "../../core/services/internationalization/internationalization.service";
import {UserService} from "../../core/services/user/user.service";

declare var moment;

enum OperationType {
  ADD = "add",
  REMOVE = "remove",
  UPDATE = "update"
}

@Component({
  selector: 'sb-resource-allocation-schedule-page',
  templateUrl: './resource-allocation-schedule-page.component.html',
  styleUrls: ['./resource-allocation-schedule-page.component.scss'],
  encapsulation: ViewEncapsulation.None, // Needed to allow us to override global boostrap4 styles
  providers: [
    DayService,
    WeekService,
    WorkWeekService,
    MonthService,
    YearService,
    AgendaService,
    MonthAgendaService,
    TimelineViewsService,
    TimelineMonthService,
    TimelineYearService,
    CheckBoxSelectionService,
    DragAndDropService,
    ResizeService,
    ICalendarExportService,
    ICalendarImportService,
    ExcelExportService
  ],
})
export class ResourceAllocationSchedulePageComponent implements  OnInit, AfterViewInit, OnDestroy {

  QUERY_EMPTY = '{"and":[]}';
  QUERY_EMPTY_JSON = {"and":[]};
  QUERY_USER = "User";
  QUERY_GROUP = "Group";
  QUERY_INDIVIDUAL_USER = "IndividualUser";
  QUERY_INDIVIDUAL_GROUP = "IndividualGroup";
  QUERY_LIST = "List";

  // TODO: this is a hack for the filters, they should come from the config
  // Hard coded fields for Assignee and AssignmentGroup

  QUERY_FILTER_TABLE_CLASS_TO_FIELD_MAPPING = {
    "User": "Assignee",
    "Group": "AssignmentGroup"
  };

  // TODO: this is a hack for updating the DB and should be in the config
  RESOURCE_ASSIGNEE = "Assignee";
  RESOURCE_ASSIGNMENT_GROUP = "AssignmentGroup";

  @ViewChild('schedule') schedule: ScheduleComponent;

  @ViewChild('filterAccordion') public filterAccordion: AccordionComponent;

  @ViewChild('filterTabs') public filterTabs: TabComponent;

  @ViewChild('treeView') public treeView: TreeViewComponent;

  @ViewChild("treeSearch") treeSearch: MaskedTextBoxComponent;

  @ViewChild('leftPanel') public leftPanel: ElementRef;

  @ViewChild('rightPanel') public rightPanel: ElementRef;

  // Component data

  public title = ''; // name of ScheduleConfiguration

  public subtitle = ''; // description of ScheduleConfiguration

  // The 'toggleSchedule' flag is there because Syncfusion Schedule really wants to have much of its
  // configuration defined up front.
  // If we want to change things like views, resources, groups, etc we have to do it
  // in one tick and then toggle the toggleSchedule flag to tear down a copy of Schedule
  // and build a new one. Note that for things that are commonly changed, such as the
  // current view or the current date, the same problem does not arise - it appears to only be
  // reference data related.
  public toggleSchedule = false;

  // The 'configurationReady' flag is to ensure that no attempt is made to build Schedule until
  // we have received our configuration data from the server.
  public configurationReady = false;

  // Should we accept live updates from the server
  public liveUpdate = true;

  // Should we obey grouping info in the configuration?
  public grouping = true;

  // Should we merge all resource configuration into a single grouping tree?
  public mergeGrouping = true;

  // Component data for 'schedule'

  // The current synthetic ScheduleModel built from the current configuration
  public scheduleModel: ScheduleModel = {};

  public eventSettings: EventSettingsModel = {};

  public group: GroupModel = {};

  public nonGroupGrouping: GroupModel = {
    byDate: false,
    byGroupID: false,
    allowGroupEdit: false,
    resources: []
  };

  public emptyGroup: GroupModel = {};

  public timezone: string = 'UTC';

  // Component data for 'filterAccordion'

  public expandMode: ExpandMode = "Multiple";

  public filterAccordionAnimation: AccordionAnimationSettingsModel = {
    collapse: {
      effect: 'SlideUp',
      duration: 100,
      easing: 'linear'
    },
    expand: {
      effect: 'SlideDown',
      duration: 100,
      easing: 'linear'
    }
  };

  public tabAnimation: TabAnimationSettingsModel = {
    previous: {
      effect: 'None'
    },
    next: {
      effect: 'None'
    }
  };

  // Filter support

  public queryFilterConfig: { [key: string]: KnockoutFieldModel } = {};

  // Set up a mechanism for us to communicate with the filters
  eventsSubject: Subject<any> = new Subject<any>();

  // TreeView support

  public treeViewRecords: FieldsSettingsModel = {
    id: 'Id',
    text: 'Subject',
    dataSource: [],
  };

  public isTreeItemDropped = false;
  public droppedItemId = '';
  public droppedItem: any = null;

  public eventDragArea = "body";

  public currentView = "TimelineDay";

  // Private data

  private queryFilterCurrentQuery: { [key: string]: string }= {
  };

  // The query builder will call the value change callback a couple of times as it is being
  // initialised - we need to ignore them until it has been stabilised.
  private queryFilterIgnoreValueChange: { [key: string]: boolean }= {};

  // The table class of the filter, which comes from the event configuration
  // We give it a default value of User so that the knockout field can build before
  // the schedule configuration is loaded
  queryFilterTableClass: string = "User";

  // If a query comes in via the params we don't know what the table class is until initialiseFilters()
  queryFilterFromParams: string = "";

  private componentFirstDisplay = true;

  private activatedRouteSubscription: Subscription | null;

  private scheduleContentChangedSubscription: Subscription;

  // Id of the current schedule configuration
  private scheduleId: string;

  // Key of the current schedule configuration
  private scheduleKey: string;

  // Optional aspect coming from the path
  private urlAspect: string;

  // The current schedule configuration from the server
  public scheduleConfiguration: ScheduleConfigurationModel;

  // The current set of raw records loaded from the event datasources
  private originalScheduleRecords: any[] = [];

  // The current set of raw records that we are showing in the tree view
  private originalTreeViewRecords: any[] = [];

  // The current set of filtered records that we are showing in the tree view
  private filteredTreeViewRecords: any[] = [];

  // The current set of raw records that we are showing in the current view
  private records: any[] = [];

  private eventDateMappings: any = {};

  // Resize support

  @ViewChild("raschedWrapper", {static: false})
  private raschedWrapper: ElementRef;

  private resizeObserver: ResizeObserver;

  constructor(private activatedRoute: ActivatedRoute,
              private utilService: UtilService,
              private requestService: RequestService,
              private scheduleDataService: ScheduleDataService,
              private navigateService: LegacyNavigateService,
              private i18nService: InternationalizationService,
              private featureFlagsService: FeatureFlagsService,
              private vcRef: ViewContainerRef,
              private zone: NgZone,
              private userService: UserService) { }

  async ngOnInit() {

    // Wipe out the page context
    (window as any).KO_PAGE_CONTEXT.clear();

    // Wipe out the Knockout content.
    const pageContentMain = document.getElementById("page-content-main");
    if (pageContentMain) {
      pageContentMain.innerHTML = "";
    }
    const errorConsole = document.getElementById("error-console");
    if (errorConsole) {
      errorConsole.innerHTML = "";
    }

    // Mark as angular page
    document.getElementById("detailMain")?.classList?.add("sb-schedule-page-content");

    // Identify the displayType from the route.
    this.activatedRouteSubscription = this.activatedRoute.paramMap.subscribe(async params => {
      this.scheduleId = params.get("id"); // 'id' of ScheduleConfiguration or a key, to be determined at loadConfiguration
      this.urlAspect = params.get("aspect") || null; // Optional aspect

      const allQueryParams = (this.activatedRoute.queryParams as any).value;
      Object.keys(allQueryParams).forEach((param) => {
        const paramValue = allQueryParams[param];
        const suffix = "Query";
        if (param.endsWith(suffix)) {
          const kind = param.substring(0, param.length - suffix.length);
          this.queryFilterCurrentQuery[kind] = paramValue;
        } else if (param === "query") {
          // query by itself is stored until we know what the table class is
          this.queryFilterFromParams = paramValue;
        }
      });

      // We may be changing routes on the same Schedule component so we need to clear it all first.
      if (!this.componentFirstDisplay) {
        // TODO some kind of clean up activity might be needed, such as clearing state.
      }

      // We may be getting filters coming in the URL
      this.initialiseFilters();
    });

    // Subscribe to service receiving schedule changes from the server
    this.scheduleContentChangedSubscription = this.scheduleDataService.scheduleContentChanged.subscribe(async (_) => {
      if (this.liveUpdate && this.scheduleModel.liveUpdate) {
        if (!this.componentFirstDisplay) {
          this.onRefreshData();
        }
      }
    });

    await this.loadConfiguration();

    if (this.scheduleConfiguration != null) {
      await this.applyConfiguration();
    } else {
      window["DialogAPI"].message("Error", "Error: Could not find schedule [" + this.scheduleId + "]" );
    }
    this.configurationReady = true;

    // Resize support
    this.resizeObserver = new ResizeObserver((entries, observer) => {
      this.zone.run(() => {
        entries.forEach((e) => {
          if (e.target === document.body) {
            if (this.schedule && this.raschedWrapper) {
              const requiredHeight = (e.contentRect.height - this.raschedWrapper.nativeElement.getBoundingClientRect().top - 120) + "px";
              this.leftPanel.nativeElement.style.height = requiredHeight;
              this.rightPanel.nativeElement.style.height = requiredHeight;
            }
          }
        });
      });
    });
    this.resizeObserver.observe(document.body);
  }

  ngAfterViewInit() {
    this.componentFirstDisplay = false;
  }

  ngOnDestroy() {
    this.scheduleContentChangedSubscription && this.scheduleContentChangedSubscription.unsubscribe();
  }

  private initialiseFilters() {

    // This table depends on which event source is loaded, we default to User as it is unlikely to be used in this component.
    // The problem is that the filter might not like to have an invalid Table in the configuration.
    //
    // The assumption here is that the first item in the list is the one to display on the right hand list
    // this requires that th order is set up correctly.
    const tableClassFromConfig = this?.scheduleConfiguration?.EventConfiguration?.[0]?.Table?.value;
    const tableClass = tableClassFromConfig || 'User';

    if (tableClassFromConfig) {
      this.queryFilterTableClass = tableClass;
      if (this.queryFilterFromParams) {
        this.queryFilterCurrentQuery[tableClass] = this.queryFilterFromParams;
      }
    }

    this.queryFilterIgnoreValueChange[tableClass] = true;
    this.queryFilterIgnoreValueChange[this.QUERY_GROUP] = true;
    this.queryFilterIgnoreValueChange[this.QUERY_USER] = true;

    this.queryFilterConfig[tableClass] = {
      name: 'String',
      datatype: 'string',
      availableValues: [],
      referenceTable: null,
      readonly: false,
      mandatory: false,
      enablePreview: false,
      tooltip: null,
      data: {
        defaultValue: null, // null | {value?: string, displayValue?: string };
        defaultValueSource: 'script', // 'nlu' | 'function' | 'question' | 'script'; // Note: script also implies it is a constant
        answer: {
          value: this.queryFilterCurrentQuery[tableClass],
          displayValue: this.queryFilterCurrentQuery[tableClass]
        }, // null | {value?: string, displayValue?: string };
      },
      changed: null,
      requestId: null,
      table: 'SyntheticTable',
      renderer: 'condition',
      rendererProperties: {
        "dependent.table": tableClass,
        onApplyFilter:  (value) => this.queryOnApplyFilter(value, tableClass),
        "update.model.on.change": true,
        showFooter: true,
        showApply:true,
        showCancel:false,
      },
      valueChangeCallback: (value) => this.queryValueChangeCallback(value, tableClass),
    };

    this.queryFilterConfig[this.QUERY_GROUP] = {
      name: 'String',
      datatype: 'string',
      availableValues: [],
      referenceTable: null,
      readonly: false,
      mandatory: false,
      enablePreview: false,
      tooltip: null,
      data: {
        defaultValue: null, // null | {value?: string, displayValue?: string };
        defaultValueSource: 'script', // 'nlu' | 'function' | 'question' | 'script'; // Note: script also implies it is a constant
        answer: {
          value: this.queryFilterCurrentQuery[this.QUERY_GROUP],
          displayValue: this.queryFilterCurrentQuery[this.QUERY_GROUP]
        }, // null | {value?: string, displayValue?: string };
      },
      changed: null,
      requestId: null,
      table: 'SyntheticTable',
      renderer: 'condition',
      rendererProperties: {
        "dependent.table": "Group",
        onApplyFilter:  (value) => this.queryOnApplyFilter(value, this.QUERY_GROUP),
        "update.model.on.change": true,
        showFooter: true,
        showApply:true,
        showCancel:false,
      },
      valueChangeCallback: (value) => this.queryValueChangeCallback(value, this.QUERY_GROUP),
    };

    this.queryFilterConfig[this.QUERY_USER] = {
      name: 'String',
      datatype: 'string',
      availableValues: [],
      referenceTable: null,
      readonly: false,
      mandatory: false,
      enablePreview: false,
      tooltip: null,
      data: {
        defaultValue: null, // null | {value?: string, displayValue?: string };
        defaultValueSource: 'script', // 'nlu' | 'function' | 'question' | 'script'; // Note: script also implies it is a constant
        answer: {
          value: this.queryFilterCurrentQuery[this.QUERY_USER],
          displayValue: this.queryFilterCurrentQuery[this.QUERY_USER]
        }, // null | {value?: string, displayValue?: string };
      },
      changed: null,
      requestId: null,
      table: 'SyntheticTable',
      renderer: 'condition',
      rendererProperties: {
        "dependent.table": "User",
        onApplyFilter:  (value) => this.queryOnApplyFilter(value,this.QUERY_USER),
        "update.model.on.change": true,
        showFooter: true,
        showApply:true,
        showCancel:false,
      },
      valueChangeCallback: (value) => this.queryValueChangeCallback(value, this.QUERY_USER),
    };

    this.queryFilterConfig[this.QUERY_INDIVIDUAL_GROUP] = {
      name: "Group",
      i18nKey: "Group",
      datatype: 'many_to_many',
      availableValues: [],
      referenceTable: "Group",
      readonly: false,
      mandatory: false,
      enablePreview: false,
      tooltip: null,
      data: {
        defaultValue: null, // null | {value?: string, displayValue?: string };
        defaultValueSource: 'script', // 'nlu' | 'function' | 'question' | 'script'; // Note: script also implies it is a constant
        answer: null, // null | {value?: string, displayValue?: string };
      },
      requestId: null,
      table: 'SyntheticTable',
      renderer: "onetomany",
      rendererProperties: {},
      valueChangeCallback: (value) => {
        this.queryValueChangeCallback(value, this.QUERY_INDIVIDUAL_GROUP);
        this.onRefreshData(null);
      },
    };

    this.queryFilterConfig[this.QUERY_INDIVIDUAL_USER] = {
      name: "User",
      i18nKey: "User",
      datatype: 'many_to_many',
      availableValues: [],
      referenceTable: "User",
      readonly: false,
      mandatory: false,
      enablePreview: false,
      tooltip: null,
      data: {
        defaultValue: null, // null | {value?: string, displayValue?: string };
        defaultValueSource: 'script', // 'nlu' | 'function' | 'question' | 'script'; // Note: script also implies it is a constant
        answer: null, // null | {value?: string, displayValue?: string };
      },
      requestId: null,
      table: 'SyntheticTable',
      renderer: "onetomany",
      rendererProperties: {},
      valueChangeCallback: (value) => {
        this.queryValueChangeCallback(value, this.QUERY_INDIVIDUAL_USER);
        this.onRefreshData(null);
      },
    };

    this.queryFilterConfig[this.QUERY_LIST] = {
      name: 'String',
      datatype: 'string',
      availableValues: [],
      referenceTable: null,
      readonly: false,
      mandatory: false,
      enablePreview: false,
      tooltip: null,
      data: {
        defaultValue: null, // null | {value?: string, displayValue?: string };
        defaultValueSource: 'script', // 'nlu' | 'function' | 'question' | 'script'; // Note: script also implies it is a constant
        answer: {
          value: this.queryFilterCurrentQuery[this.QUERY_LIST],
          displayValue: this.queryFilterCurrentQuery[this.QUERY_LIST]
        }, // null | {value?: string, displayValue?: string };
      },
      changed: null,
      requestId: null,
      table: 'SyntheticTable',
      renderer: 'condition',
      rendererProperties: {
        "dependent.table": tableClass,
        onApplyFilter:  (value) => this.queryOnApplyFilter(value, this.QUERY_LIST),
        "update.model.on.change": true,
        showFooter: true,
        showApply:true,
        showCancel:false,
      },
      valueChangeCallback: (value) => this.queryValueChangeCallback(value, this.QUERY_LIST),
    };

  }

  // Fake button navigation

  public buttonLabel(key: string): string {
    return this.i18nService.getMessage(key);
  }

  public canGoBack() {
    return this.navigateService.canGoBack();
  }

  public back() {
    this.navigateService.back();
  }

  // Formatting

  public getValue(data: any, field: string) {
    return data?.record?.[field]?.value || data?.record?.['C_' + field]?.value;
  }

  public getDisplayValue(data: any, field: string) {
    return data?.record?.[field]?.displayValue || data?.record?.['C_' + field]?.displayValue;
  }

  // Event handlers

  public onScheduleCreated(arg: any) {
    // Some configuration is set as inputs to the angular component in the template, while others are set here. The ones set
    // here are conditional because the schedule component changes behaviour depending on whether they are set or not,
    // even if you set it to the default value or undefined or null, and you don't get a choice when they are done in the template.

    if (this.scheduleModel.selectedDate) {
      this.schedule.selectedDate = this.scheduleModel.selectedDate;
    }
    if (this.scheduleModel.minDate) {
      this.schedule.minDate = this.scheduleModel.minDate;
    }
    if (this.scheduleModel.maxDate) {
      this.schedule.maxDate = this.scheduleModel.maxDate;
    }
    if (this.scheduleModel.dateFormat) {
      this.schedule.dateFormat = this.scheduleModel.dateFormat;
    }

    // TODO: might need to be in dataBound
    Object.keys(this.queryFilterIgnoreValueChange).forEach(k => this.queryFilterIgnoreValueChange[k] = false);

    // Setting the list node template
    this.treeView.nodeTemplate = this.scheduleModel.nodeTemplate;
  }

  public onActionBegin(args: ActionEventArgs & ToolbarActionArgs) {
    if (args.requestType === 'toolbarItemRendering') {

      // const clearFilters: ItemModel = {
      //   align: 'Left', showTextOn: 'Overflow', prefixIcon: 'fa fa-plus',
      //   template:   `<span class="fa-stack fa-1x">
      //                   <i class="fa fa-filter fa-stack-1x"></i>
      //                   <i class="fa fa-ban fa-stack-2x sb-rasched-clear-filters-colour"></i>
      //                </span>`,
      //   tooltipText: `Clear filters`,
      //   text: 'Clear filters',
      //   click: this.onClearFilters.bind(this)
      // };

      const timelineSeparator: ItemModel = {
        align: 'Right', type: 'Separator'
      };
      const separator: ItemModel = {
        align: 'Right', type: 'Separator'
      };
      const createRecord: ItemModel = {
        align: 'Right', showTextOn: 'Both', prefixIcon: 'e-plus',
        tooltipText: `Create ${this.queryFilterTableClass}`,
        click: this.onCreateRecord.bind(this)
      };
      const refreshData: ItemModel = {
        align: 'Right', showTextOn: 'Both', prefixIcon: 'e-refresh',
        tooltipText: 'Refresh data',
        click: this.onRefreshData.bind(this)
      };
      // const refreshAll: ItemModel = {
      //   align: 'Right', showTextOn: 'Both', prefixIcon: 'e-refresh sb-rasched-refresh-all-colour',
      //   tooltipText: 'Refresh configuration',
      //   click: this.onRefreshAll.bind(this)
      // };
      const toggleSettings: ItemModel = {
        align: 'Right', showTextOn: 'Both', prefixIcon: 'e-settings',
        tooltipText: 'Settings',
        click: this.onSettingsClick.bind(this)
      };

      args.items.splice(args.items.findIndex((item) => item.text === 'Timeline Day'), 0, timelineSeparator);

      // args.items.push(clearFilters);

      args.items.push(separator);
      args.items.push(createRecord);
      args.items.push(refreshData);
      // args.items.push(refreshAll);
      args.items.push(toggleSettings);

    } else if (args.requestType === 'eventCreate' && this.isTreeItemDropped) {

      const treeViewData: Record<string, any>[] = this.treeView.fields.dataSource as Record<string, any>[];
      const filteredData: Record<string, any>[] = treeViewData.filter((item: any) => item.Id !== this.droppedItemId);
      this.treeView.fields.dataSource = filteredData;
      const elements: NodeListOf<HTMLElement> = document.querySelectorAll('.e-drag-item.treeview-external-drag');
      for (const element of [].slice.call(elements)) {
        remove(element);
      }
      this.isTreeItemDropped = false;

      // At this point we can fix up the event data as the editor only supplies the ones it has fields for on the form
      args.addedRecords.forEach((r) => {
        r.Guid = r.Id;
        r.Id = r._Id;
        delete r._Id;
        r.record = cloneDeep(this.droppedItem.record);
      });

      // Add the relationships
      args.addedRecords.forEach((r) => {
        Object.keys(this.droppedItem).filter((key) => key.endsWith("Id")).forEach((key) => {
          if (!r[key]) {
            r[key] = cloneDeep(this.droppedItem[key]);
          }
        });
      });

      // This is dropping an event onto the schedule, which gives us values in args.addedRecords[0] to update the DB with
      const updateData = args.addedRecords[0];
      this.updateDBRecord(updateData, OperationType.ADD);

    } else if (args.requestType === 'eventCreate' && !this.isTreeItemDropped) {
      args.cancel = true;
      DialogUtility.alert({
        title: 'Create event denied',
        content: `Events cannot be created in this schedule, they can only be drag and dropped from the record list.`,
        isModal: true,
        isDraggable: false,
        showCloseIcon: true,
        closeOnEscape: true,
        position:  { X: 'center', Y: 'center' },
        okButton: {
          text: "OK"
        }
      });

    } else if (args.requestType === 'eventChange') {
      // This is updating an event onto the schedule, which gives us values in args.changedRecords[0] to update the DB with
      const updateData = args.changedRecords[0];

      if (!updateData.AssigneeId) {
        args.cancel = true;
        this.displayEventNotDroppedOnResourceDialog();
      } else {
        this.updateDBRecord(updateData, OperationType.UPDATE);
      }

    } else if (args.requestType === 'eventRemove') {

      // This is deleting an event on the schedule, which really just sets Assignee, AssignmentGroup, StartTime and EndTime to null
      // This can come a couple of places in the schedule UI, so the record is sometimes args.deletedRecords[0] and sometimes args.changedRecords[0]
      let deleteData = args.deletedRecords?.[0] || args.changedRecords?.[0];
      deleteData = cloneDeep(deleteData); // When modifying data passed to us to pass on we have to clone it.
      deleteData.AssigneeId = null;
      deleteData.AssignmentGroupId = null;
      deleteData.StartTime = null;
      deleteData.EndTime = null;
      this.updateDBRecord(deleteData, OperationType.REMOVE);
    }

  }

  public async onCreateRecord(args: ClickEventArgs) {
    var tileOptions = {
      "tile": "DetailsAutoScaffold",
      "parameter-values":
        {
          table: this.queryFilterTableClass,
          method: "metadata"
        },
      loadOptions: {
        cache: false,
      },
      title: ""
    };

    this.navigateService.modalForm(tileOptions);
    // this.navigateService.to({urlElements: [this.queryFilterTableClass, '_create']});
  }

  public async onClearFilters(args: ClickEventArgs) {
    this.eventsSubject.next('clear');
  }

  public async onRefreshData(args?: ClickEventArgs) {
    await this.refreshResources();
    await this.loadRecords();
    this.applyRecords();
  }

  public async onRefreshAll(args: ClickEventArgs) {
    await this.loadConfiguration();
    await this.loadRecords();
    await this.applyConfiguration();
    this.applyRecords();
    this.toggleScheduleComponent();
  }

  public onGroupingChange(args: SwitchEventArgs): void {
    this.grouping = args.checked;
    this.onRefreshAll(null);
  }

  public onLiveUpdateChange(args: SwitchEventArgs): void {
    this.liveUpdate = args.checked;
  }

  public onDataBound(args: any) {
  }

  public onEventRendered(args: any) {
    let categoryColor: string = args.data.CategoryColor as string;
    if (!args.element || !categoryColor) {
      return;
    }
    if (this.schedule.currentView === 'Agenda') {
      (args.element.firstChild as HTMLElement).style.borderLeftColor = categoryColor;
    } else {
      args.element.style.backgroundColor = categoryColor;
    }
  }

  public onActionComplete(args: any) {
    // The toolbar and date picker overlap on first rendering. This refresh causes the toolbar
    // position to be recalculated so they don't overlap
    if (args.requestType == "toolBarItemRendered") {
      this.utilService.tickThen(() => {
        this.onRefreshData();
      })
    } else if (args.requestType == 'viewNavigate') {
      // Filter out the IsBlock events in the non timeline views
      const filteredRecords = !this.isTimelineView() ? this.records.filter((r) => !r.IsBlock) : this.records;

      this.eventSettings.dataSource = filteredRecords;
      this.eventSettings = this.cloneEventSettings( this.eventSettings);
    }
  }

  public onActionFailure(args: any) {
  }

  public onSettingsClick(args): void {
    const settingsPanel: Element = document.querySelector('.sb-rasched-content .right-panel');
    if (settingsPanel.classList.contains('hide')) {
      removeClass([settingsPanel], 'hide');
    } else {
      addClass([settingsPanel], 'hide');
    }
    this.schedule.refreshEvents();
  }

  resourceHeaderLink(data: ResourceDetails) {
    const id = data.resourceData.Id;

    const tableName = this.isSchedule(id) ? id : data.resourceData.record.TableClass.value;
    const tableID = this.isSchedule(id) ? "" : `/${id}` ;
    const aspect = this.urlAspect ? `/${this.urlAspect}` : "";
    return `/#/${tableName}${tableID}${aspect}`;
  }

  // An event that is not assignable, e.g. a leave period.
  // Some of these have an Id like BlackoutSchedule, others have the IsSynthetic property.
  isSyntheticEvent(data: ScheduleEventModel | ResourceDetails | Record<string, any>) {
    const id = data.resourceData?.Id || (data as any).Id;
    const isSynthetic = (data as any).IsSynthetic;
    return (id && id.endsWith("Schedule")) || isSynthetic;
  }

  isBlockSyntheticEvent(data: ScheduleEventModel | ResourceDetails | Record<string, any>) {
    return this.isSyntheticEvent(data) && (data as ScheduleEventModel).IsBlock;
  }

  isNonBlockSyntheticEvent(data: ScheduleEventModel | ResourceDetails | Record<string, any>) {
    return this.isSyntheticEvent(data) && !(data as ScheduleEventModel).IsBlock;
  }

  isNonSyntheticEvent(data: ScheduleEventModel | ResourceDetails | Record<string, any>) {
    return !this.isSyntheticEvent(data);
  }

  isAssignableEvent(data: ScheduleEventModel | ResourceDetails | Record<string, any>) {
    return this.isNonSyntheticEvent(data);
  }

  private isSchedule(id: string) {
    return id && id.endsWith("Schedule");
  }

  // Private data support

  private applyRecords() {
    const records = this.processRecords(this.scheduleModel);

    const model = this.scheduleModel.eventSettings;

    model.dataSource = records;

    this.eventSettings = this.cloneEventSettings(model);

    // The filters might be dependent on the configuration
    this.initialiseFilters();

    // Update the right hand list
    this.applyTreeFilters();
  }

  private async applyConfiguration() {
    this.scheduleModel = await this.processConfiguration();

    this.mergeGrouping = this.scheduleModel.mergeGrouping.enabled;

    this.liveUpdate = this.scheduleModel.liveUpdate;

    this.group = this.scheduleModel.group;
    if (!this.grouping) {
      this.group.resources = [];
    }

    this.timezone = this.scheduleModel.timezone || this.userService.getTimeZone();

    // The filters might be dependent on the configuration
    this.initialiseFilters();

    // Update the right hand list
    this.applyTreeFilters();
  }

  // Private methods for data

  private async loadRecords() {
    try {
      const combinedCurrentQuery = this.possiblyCombineQueries();
      this.records = await this.scheduleDataService.loadRecords(this.scheduleConfiguration, combinedCurrentQuery);
    } catch (e) {
      console.error("ResourceAllocationSchedulePageComponent", e);
    }
  }

  private possiblyCombineQueries() {
    if (this.isTimelineView()) {
      return this.queryFilterCurrentQuery;
    } else {
      const baseCondition = this.queryFilterCurrentQuery[this.queryFilterTableClass];
      const baseQuery = baseCondition ? JSON.parse(baseCondition) : JSON.parse(this.QUERY_EMPTY);
      const otherSubQueries = Object.keys(this.queryFilterCurrentQuery).filter(k => k !== this.queryFilterTableClass).map((k) => {
        const relationName = this.QUERY_FILTER_TABLE_CLASS_TO_FIELD_MAPPING[k];
        const condition = this.queryFilterCurrentQuery[k] ? this.queryFilterCurrentQuery[k] : this.QUERY_EMPTY;
        return {"relationName": relationName, "operator": "subQuery", "criteria": JSON.parse(condition)};
      });
      const result = {};
      result[this.queryFilterTableClass] = JSON.stringify({"and":  [baseQuery, ...otherSubQueries]});
      return result;
    }
  }

  private processRecords(scheduleModel: ScheduleModel): any[] {
    // Make the dates into JS Date objects
    this.records.forEach((r) => {
      if (r.StartTime) {
        if (Array.isArray(r.StartTime)) {
        // This is a date not a dateTime
          r.StartTime = moment(new Date(r.StartTime)).startOf("day").toDate()._dateProxy;
        } else {
          r.StartTime = new Date(r.StartTime);
        }
      }
      if (r.EndTime) {
        if (Array.isArray(r.EndTime)) {
          // This is a date not a dateTime
          r.EndTime = moment(new Date(r.EndTime)).endOf("day").toDate()._dateProxy;
        } else {
          r.EndTime = new Date(r.EndTime);
        }
      }
    });

    // Add formatted parts to records to avoid the need for functions in templates.
    this.records.forEach((r) => {
        r.fmt  = r.fmt || {};
        r.fmt.eventFormattedSubject = this.getEventFormattedSubject(r);
        r.fmt.eventFormattedTimeString = this.getEventFormattedTimeString(r);
        r.fmt.eventFormattedStatus = this.getEventFormattedStatus(r);
    });

    // Split the records into two groups:
    // Schedule records are assignable and have an assignment group and assignee, or are synthetic, and must have a start/end date
    // TreeView records are assignable and don't have an assignee or a start/end date

    const allocateToTreeView = (r: any): boolean => {
      return this.isAssignableEvent(r) && (!r.record.Assignee.value || !r.StartTime || !r.EndTime);
    }

    const allocateToScheduleView = (r: any): boolean => {
      return (this.isAssignableEvent(r) && !!r.record.Assignee.value && !!r.record.AssignmentGroup.value && !!r.StartTime && !!r.EndTime) || this.isSyntheticEvent(r);
    }

    this.treeViewRecords = {
      id: 'Id',
      text: 'Subject',
      dataSource: this.records.filter((r) => allocateToTreeView(r)),
    };

    this.originalTreeViewRecords = cloneDeep(this.treeViewRecords.dataSource);
    this.filteredTreeViewRecords = cloneDeep(this.treeViewRecords.dataSource);

    this.originalScheduleRecords = this.records;

    this.records = this.originalScheduleRecords.filter((r) => allocateToScheduleView(r));

    // Do merge grouping
    if (this.mergeGrouping && scheduleModel?.mergeGrouping?.enabled) {
      this.records.forEach((r)=> {
        scheduleModel.mergeGrouping.eventForeignIdFields.forEach((f) => {
          if (r[f]) {
            if (!r["_mergedField"]) {
              r["_mergedField"] = [];
            }
            r["_mergedField"].push(r[f]);
          }
        });
        Object.keys(scheduleModel.mergeGrouping.eventParentIds).forEach((k) => {
          const rec = scheduleModel.mergeGrouping.eventParentIds[k];
          if (r[k]) {
            r[rec.id] = rec.value;
          }
        });
      });
    }

    return this.records;
  }

  // Private methods for configuration

  private getConfig(): ScheduleConfigurationModel {
    return this.scheduleConfiguration;
  }

  private async loadConfiguration() {
    const config = await this.scheduleDataService.loadConfiguration(this.scheduleId);
    this.scheduleConfiguration = config.scheduleConfiguration;
    this.scheduleId = config.scheduleId;
    this.scheduleKey = config.scheduleKey;
    this.eventDateMappings = config.eventDateMappings;
  }

  private async processConfiguration() {

    const options: TemplateCompilerOptions = {};
    if (this.featureFlagsService.isOn("schedule-angular-debugging-display")) {
      options.debugStyle = "border: 1px dotted red";
    }

    const {title, subtitle, schedule} = await this.scheduleDataService.processConfiguration(this.getConfig(), this.vcRef, this, options);

    this.title = title;
    this.subtitle = subtitle;

    this.currentView = schedule.currentView || "TimelineDay";

    const relevantQueries = cloneDeep(this.queryFilterCurrentQuery);
    delete relevantQueries[this.QUERY_LIST];

    let resources = await this.scheduleDataService.processScheduleModel(schedule, relevantQueries);

    resources = this.patchResources(resources);
    return resources;
  }

  private async refreshResources() {
    const relevantQueries = cloneDeep(this.queryFilterCurrentQuery);
    delete relevantQueries[this.QUERY_LIST];

    let modelWithNewResources = await this.scheduleDataService.processScheduleModel(this.scheduleModel, relevantQueries);

    modelWithNewResources = this.patchResources(modelWithNewResources);

    this.scheduleModel.resources = modelWithNewResources.resources;

    // Refresh the schedule in place
    this.schedule.resources.forEach((r) => {
      const name = r.name;
      const newResource = this.scheduleModel.resources.find((r) => r.name === name);
      const newDataSource = newResource?.dataSource || [];
      r.dataSource = newDataSource;
    });
  }

  // Filter support

  // Value is json query
  queryOnApplyFilter(value: any, whichQuery: string) {
    this.queryFilterCurrentQuery[whichQuery] = JSON.stringify(value);
    this.queryFilterConfig[whichQuery].data.answer.value = this.queryFilterCurrentQuery[whichQuery];
    this.queryFilterConfig[whichQuery].data.answer.displayValue = this.queryFilterCurrentQuery[whichQuery];

    this.onRefreshData();
  }

  // Value is serialised query
  queryValueChangeCallback(value: string | string[], whichQuery: string) {
    if (this.queryFilterIgnoreValueChange[whichQuery]) {
      console.debug('ResourceAllocationSchedulePageComponent.queryValueChangeCallback - ignoring value change as not initialised yet', value, whichQuery);
    } else {
      if (whichQuery == this.QUERY_INDIVIDUAL_GROUP) {
        this.queryFilterCurrentQuery[this.QUERY_GROUP] = value ? `{"and": [{"field": "id", "operator": "in", "value": [${(value as string).split(',').map(v => `"${v}"`)}]}]}` : this.QUERY_EMPTY;
      } else if (whichQuery == this.QUERY_INDIVIDUAL_USER) {
        this.queryFilterCurrentQuery[this.QUERY_USER] = value ? `{"and": [{"field": "id", "operator": "in", "value": [${(value as string).split(',').map(v => `"${v}"`)}]}]}` : this.QUERY_EMPTY;
      } else {
        this.queryFilterCurrentQuery[whichQuery] = value as string;
      }
    }
  }

  // Utilities

  private toggleScheduleComponent() {
    this.toggleSchedule = !this.toggleSchedule;
  }

  public toLowerCase(s: string) {
    return s ? s.toLowerCase() : "";
  }

  private async getControllerData(method: string, data: any = {}) {
    const response: any = await this.requestService.createRequest()
      .controller("servicely.core.Schedule")
      .method(method)
      .data(data)
      .execute();

    return response.data;
  }

  private async updateDBRecord(updateData: any, operation: OperationType) {
    // Real table class of the record, the event config might be for a parent table
    const tableClass = updateData.record.TableClass.value;
    // Event configuration that generated this record
    const eventConfigId = updateData.configId;
    // Date type that is applicable to this event configuration
    const dateType = this.eventDateMappings ? this.eventDateMappings[eventConfigId] : 'DATE_TIME';

    const recordId = updateData.Id;
    let startTime = null;// JS Date
    if (updateData.StartTime) {
      if (dateType === 'DATE_TIME') {
        startTime = (updateData.StartTime as Date).toISOString();
      } else {
        startTime = moment(updateData.StartTime as Date).format("YYYY-MM-DD");
      }
    }
    let endTime = null;
    if (updateData.EndTime) {
      if (dateType === 'DATE_TIME') {
        endTime = (updateData.EndTime as Date).toISOString();
      } else {
        // We subtract one second so that the correct date is used. Syncfusion component
        // sends the time portion if dragged, but doesn't if you edit it via the popup.
        endTime = moment(updateData.EndTime as Date).subtract(1, 's').format("YYYY-MM-DD");
      }
    }
    const assigneeId = Array.isArray(updateData.AssigneeId) ? updateData.AssigneeId?.[0] : updateData.AssigneeId; // Sometimes comes in as an array or a scalar
    const assignmentGroupId = Array.isArray(updateData.AssignmentGroupId) ? updateData.AssignmentGroupId?.[0] : updateData.AssignmentGroupId; // Sometimes comes in as an array or a scalar; updateData.AssignmentGroupId;
    const subject = updateData.Subject;
    const description = updateData.Description;

    const applicableEventConfiguration = this.scheduleConfiguration.EventConfiguration.find((rec) => rec.id == eventConfigId);

    const startTimeField = applicableEventConfiguration.EventStartTimeField.value;
    const endTimeField = applicableEventConfiguration.EventEndTimeField.value;
    const subjectField = applicableEventConfiguration.EventSubjectField.value;
    const descriptionField = applicableEventConfiguration.EventDescriptionField.value;

    const assigneeIdField = this.RESOURCE_ASSIGNEE;
    const assignmentGroupIdField = this.RESOURCE_ASSIGNMENT_GROUP;

    const data: any = {
      id: recordId,
    };

    if (startTimeField) {
      data[startTimeField] = startTime;
    }
    if (endTimeField) {
      data[endTimeField] = endTime;
    }
    if (descriptionField) {
      data[descriptionField] = description;
    }
    if (assigneeIdField) {
      data[assigneeIdField] = assigneeId;
    }
    if (assignmentGroupIdField) {
      data[assignmentGroupIdField] = assignmentGroupId;
    }
    if (subjectField) {
      data[subjectField] = subject;
    }

    data['operation'] = operation
    data['configId'] = this.scheduleConfiguration.id

    const response: any = await this.requestService.createRequest()
      .controller("servicely.core.Schedule")
      .method("update")
      .table(tableClass)
      .data(data)
      .execute();

    this.onRefreshData();
  }

  // TreeView support
  onTreeDragStart(event: any) {
    document.body.classList.add("sb-rasched-tree-dragging");
  }


  onTreeDragStop(event: any) {
    document.body.classList.remove("sb-rasched-tree-dragging");

    if (!this.isTimelineView()) {
      event.cancel = true;
      return;
    }

    const treeElement: Element = closest(event.target, '.e-treeview') as Element;
    const classElement: HTMLElement = this.schedule.element.querySelector('.e-device-hover');
    if (classElement) {
      classElement.classList.remove('e-device-hover');
    }
    if (!treeElement) {
      event.cancel = true;
      const scheduleElement: Element = closest(event.target, '.e-content-wrap') as Element;
      if (scheduleElement) {
        const treeViewData: Record<string, any>[] = this.treeView.fields.dataSource as Record<string, any>[];
        if (event.target.classList.contains('e-work-cells')) {
          const droppedEvent: Record<string, any>[] = treeViewData.filter((item: any) => item.Id === event.draggedNodeData.id);
          const droppedCell: CellClickEventArgs = this.schedule.getCellDetails(event.target);
          const cellResourceDetails: ResourceDetails = this.schedule.getResourcesByIndex(droppedCell.groupIndex);

          if (!cellResourceDetails.groupData.AssigneeId) {
            this.displayEventNotDroppedOnResourceDialog();
          } else {
            const eventData: Record<string, any> = {
              Id: droppedEvent[0].Id,
              _Id: droppedEvent[0].Id,
              Subject: droppedEvent[0].Subject,
              StartTime: droppedEvent[0].StartTime || droppedCell.startTime,
              EndTime: droppedEvent[0].EndTime || droppedCell.endTime,
              IsAllDay: false,
              Description: droppedEvent[0].Description,
              AssigneeId: cellResourceDetails?.resourceData?.Id, // The user we dropped onto
              AssignmentGroupId: cellResourceDetails?.resourceData?.GroupID, // The assignment group we dropped onto
            };

            this.schedule.openEditor(eventData, 'Add', true);
            this.isTreeItemDropped = true;
            this.droppedItemId = event.draggedNodeData.id as string;
            this.droppedItem = droppedEvent[0];
          }
        }
      }
    }
  }

  onPopupOpen(args: any) {
    if (args.type === 'QuickInfo') {
      if (!args.data.Id) {
        // This is an attempt to create an event by clicking, which we don't support
        args.cancel = true;
        return;
      }
    } else if (args.type == 'DeleteAlert') {
      // We don't want to display the confirm delete dialog
      this.schedule.deleteEvent(args.data);
      args.cancel = true;
    } else if (args.type === 'Editor') {
      if (!args.data.Id) {
        // This is an attempt to create an event by double clicking, which we don't support
        args.cancel = true;
        return;
      }
      // Add Id field that is hidden
      if (!args.element.querySelector('.sb-rasched-custom-id-field')) {
        let idElem: HTMLInputElement = createElement('input', {
          className: 'e-field sb-rasched-custom-id-field',
          attrs: {
            name: 'Id',
            type: 'hidden',
            value: args.data.Id
          }
        }) as HTMLInputElement;
        let _idElem: HTMLInputElement = createElement('input', {
          className: 'e-field sb-rasched-custom-_id-field',
          attrs: {
            name: '_Id',
            type: 'hidden',
            value: args.data.Id
          }
        }) as HTMLInputElement;
        let formElement: HTMLElement = args.element.querySelector('.e-schedule-form');
        formElement.firstChild.insertBefore(idElem, args.element.querySelector('.e-title-location-row'));
        formElement.firstChild.insertBefore(_idElem, args.element.querySelector('.e-title-location-row'));

        // Make Subject read only
        let subjectElement: HTMLElement = args.element.querySelector('.e-subject.e-field');
        subjectElement.setAttribute("readonly", "readonly");

        // Hide Location Container
        let locationContainer: HTMLElement = args.element.querySelector('.e-location-container');
        locationContainer.style.visibility = "hidden";
        locationContainer.style.height = "0";

        // Hide all day timezone row
        let allDayRow: HTMLElement = args.element.querySelector('.e-all-day-time-zone-row');
        allDayRow.style.visibility = "hidden";
        allDayRow.style.height = "0";

        // Hide repeat row
        let repeatRow: HTMLElement = args.element.querySelector('.e-recurrenceeditor');
        repeatRow.style.visibility = "hidden";
        repeatRow.style.height = "0";

        // Change label of delete button to Unschedule
        let deleteButton: HTMLElement = args.element.querySelector('.e-footer-content button.e-event-delete');
        deleteButton.innerHTML = 'Unschedule';

      }
      let idElem: HTMLElement = args.element.querySelector('.sb-rasched-custom-id-field');
      idElem.setAttribute("value", args.data.Id);
      let _idElem: HTMLElement = args.element.querySelector('.sb-rasched-custom-_id-field');
      _idElem.setAttribute("value", args.data.Id);
    }
  }

  onScheduleDragStop(args: DragEventArgs) {
    // if (args.target.closest('.e-treeview')) {
    if (args.target.closest('.right-panel')) {
      args.cancel = true;
      this.schedule.deleteEvent(args.data);
    }
  }

  // TODO: workaround for bug that just appeared in resources, where the top level grouping needs
  // a groupId field of Id due to a bug in the syncfusion code in a method named 'findIndexInData'
  // TODO: see if the syncfusion bug still exists
  private patchResources(resources: ScheduleModel) {
    const topLevelGroup = resources?.group?.resources?.[0];
    if (topLevelGroup) {
      resources.resources.filter(r => r.name == topLevelGroup).forEach((r => {
        r.groupIDField = 'Id';
      }));
    }

    return resources;
  }

  isTimelineView(): boolean {
    return this?.schedule?.currentView && ['TimelineDay', 'TimelineWeek', 'TimelineWorkWeek', 'TimelineMonth', 'TimelineYear'].includes(this.schedule.currentView);
  }

  displayEventNotDroppedOnResourceDialog() {
    DialogUtility.alert({
      title: 'Event not dropped on resource',
      content: `The ${this.queryFilterTableClass} record cannot be dropped because this is a group not a resource`,
      isModal: true,
      isDraggable: false,
      showCloseIcon: true,
      closeOnEscape: true,
      position: {X: 'center', Y: 'center'},
      okButton: {
        text: "OK"
      }
    });
  }

  // Preview support - very similar to table-grid, might want to make a component from it

  clickPreview(e: MouseEvent, data: ResourceDetails | Record<string, any>) {
    // Depending on where the preview is being invoked we might have different data coming in.
    // ResourceDetails is from the grouping tree in timeline views and Record<string,any> is from the
    // quick info popup.
    const resourceData = (data as ResourceDetails).resourceData || data;
    return this.clickPreviewImpl(e, resourceData);
  }

  // TODO: This mucking about with state on the element should be done with an Angular directive.
  clickPreviewImpl(e: MouseEvent, data: Record<string, any>) {

    const initClicks = (e: MouseEvent) => {
      if ((e.target as any)._clicks === null || (e.target as any)._clicks === undefined) {
        (e.target as any)._clicks = 0;
      }
    };
    const getClicks = (e: MouseEvent): number => {
      return (e.target as any)._clicks;
    };
    const setClicks = (e: MouseEvent, value: number) => {
      (e.target as any)._clicks = value;
    }

    initClicks(e);
    setClicks(e, getClicks(e) + 1);
    if (getClicks(e) === 1) {
      setTimeout(() => {
        const id = data.Id;
        if (getClicks(e) === 1) {
          if (this.isSchedule(id)) {
            this.clickScheduleAction(e, data);
          } else {
            this.clickPreviewAction(e, data);
          }
        } else {
          if (this.isSchedule(id)) {
            this.altClickScheduleAction(e, data);
          } else {
            this.altClickPreviewAction(e, data);
          }
        }
        setClicks(e, 0);
      }, 200);
    }
    return false;
  }

  clickPreviewAction(e: MouseEvent, data: Record<string, any>) {
    if (e.metaKey === true || e.altKey === true || e.ctrlKey === true) {
      this.altClickPreviewAction(e, data);
    } else {
      const tableName = data.record.TableClass.value;
      const tableID = data.Id;
      const aspect = this.urlAspect;
      const view = "Popout" + tableName;
      this.navigateService.rightSlideInWindow(
        {
          'tile': 'DetailsAutoScaffold',
          'parameter-values': {
            table: tableName,
            id: tableID,
            aspect: aspect,
            view: view
          },
          'loadOptions': {
            cache: false,
            popup: true
          }
        },
        (eventData, envelope) => {
        });
    }
  }

  altClickPreviewAction(e: MouseEvent, data: Record<string, any>) {
    const tableName = data.record.TableClass.value;
    const tableID = data.Id;
    const aspect = this.urlAspect;

    this.navigateService.to({
      table: tableName,
      id: tableID,
      aspect: aspect
    });
  }

  clickScheduleAction(e: MouseEvent, data: Record<string, any>) {
    const tableName = data.Id;
    const aspect = this.urlAspect;

    this.navigateService.to({
      table: tableName,
      aspect: aspect
    });
  }

  altClickScheduleAction(e: MouseEvent, data: Record<string, any>) {
    this.clickScheduleAction(e, data);
  }

  // Quick info support

  getQuickInfoFormattedDateString(data: Record<string, any>): string {
    // getFormattedString is a private method in QuickPopups
    // @ts-ignore
    const argsData: { eventSubject: string, details: string } = this.schedule.quickPopup.getFormattedString(data);
    return argsData.details;
  }

  getQuickInfoFormattedSubject(data: Record<string, any> ) {
    return this.isSyntheticEvent(data) ? data.Subject : `${data?.record?.Number?.displayValue}`;
  }

  getQuickInfoRecordValue(data: Record<string, any>, field: string): string {
    return data?.record?.[field]?.displayValue;
  }

  // Event template support

  getTimeString(date: Date): string {
    return date ? this.i18nService.formatDate(date, { skeleton: 'hm' }) : "";
  }

  getEventFormattedSubject(data: Record<string, any> ) {
    return this.isSyntheticEvent(data) ? data.Subject : `${data?.record?.Number?.displayValue}`;
  }

  getEventFormattedTimeString(data: Record<string, any>) {
    return `${this.getTimeString(data?.StartTime)} - ${this.getTimeString(data?.EndTime)}`;
  }

  getEventFormattedStatus(data: Record<string, any>) {
    return data?.record?.WorkflowStatus?.displayValue;
  }


  // TreeView support

  async applyTreeFilters() {

    const query = this.queryFilterCurrentQuery[this.QUERY_LIST];

    if (query != null && query !== this.QUERY_EMPTY) {
      const originalIds = this.originalTreeViewRecords.map((r) => r.Id).sort();

      const data = {
        ids: originalIds,
        query: query
      };

      const response: any = await this.requestService.createRequest()
        .controller("servicely.core.Schedule")
        .method("filterIdsByQuery")
        .table(this.queryFilterTableClass)
        .data(data)
        .execute();

      const filteredIds = response.data as string[];

      this.filteredTreeViewRecords = this.originalTreeViewRecords.filter((r) => filteredIds.includes(r.Id));

      this.treeViewRecords = {
        id: 'Id',
        text: 'Subject',
        dataSource: this.filteredTreeViewRecords,
      };
    }

    this.onTreeSearchNodes(null);
  }

  onTreeDrawNode(args: DrawNodeEventArgs) {
    let elem: HTMLElement = args.node.querySelector('.e-list-text');
    elem.style.backgroundColor = args.nodeData.CategoryColor as string;
  }

  // For setting breakpoints in dynamic templates
  debug() {
    debugger;
  }

  getInitials(data: any) {
    if (data?.AssigneeId?.[0]) {
      const record = (window as any).Table("User", data.AssigneeId[0]);
      const firstName = record.FirstName();
      const lastName = record.LastName();
      const firstInitial = (firstName && firstName).length > 0 ? firstName.charAt(0) : "";
      const lastInitial = (lastName && lastName).length > 0 ? lastName.charAt(0) : "";
      return firstInitial.toUpperCase() + lastInitial.toUpperCase();
    } else {
      return "";
    }
  }

  onTreeSearchNodes(args: ChangedEventArgs) {
    if (this.treeSearch) {
      let value = this.treeSearch.value;
      if (value == "") {
        const originalIds = this.filteredTreeViewRecords.map((r) => r.Id).sort();
        const currentIds = (this.treeViewRecords.dataSource as any[]).map((r) => r.id).sort();
        const same = originalIds.length === currentIds.length && originalIds.every((v, i) => v === currentIds[i]);
        if (!same) {
          this.changeDataSource(this.filteredTreeViewRecords);
        }
      } else {
        const candidateRecords = this.filteredTreeViewRecords.map((p: any) => {
          const text = Object
            .keys(p.record)
            .filter((k) => !["CreatedOn", "CreatedBy", "UpdatedOn", "UpdatedBy", "Version", "TableClass", "SID", "Id"].includes(k))
            .map((k) => p.record[k].displayValue)
            .filter((v) => v != "false")
            .join(" ");
          return {
            id: p.Id,
            text: text
          };
        });
        const predicate = new Predicate('text', 'contains', value, true);
        const filteredCandidateRecords = new DataManager(candidateRecords).executeLocal(new Query().where(predicate)).map(p => (p as any).id);
        const filteredRecords = this.filteredTreeViewRecords.filter((p) => filteredCandidateRecords.includes(p.Id));

        const currentIds = (this.treeViewRecords.dataSource as any[]).map((r) => r.id).sort();
        const filteredIds = filteredRecords.map((r) => r.Id).sort();
        const same = filteredIds.length === currentIds.length && filteredIds.every((v, i) => v === currentIds[i]);
        if (!same) {
          this.changeDataSource(filteredRecords);
        }
      }
    }
  }

  private changeDataSource(data: any[]) {
    this.treeViewRecords = {
      id: 'Id',
      text: 'Subject',
      dataSource: data,
    }
  }

  /**
   * Clone an event settings model, but don't clone the template as it is very expensive
   */
  private cloneEventSettings(eventSettings: EventSettingsModel) {
    const template = eventSettings.template;
    delete eventSettings.template;
    const result = cloneDeep(eventSettings);
    result.template = template;
    eventSettings.template = template;
    return result;
  }

}
